import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

interface ScrollImageSectionProps {
  imageUrl: string;
  title?: string;
  subtitle?: string;
  children?: React.ReactNode;
  overlayOpacity?: number;
  minHeight?: string;
  imageAlt?: string;
}

export const ScrollImageSection = ({
  imageUrl,
  title,
  subtitle,
  children,
  overlayOpacity = 0.6,
  minHeight = '100vh',
  imageAlt = 'Section background'
}: ScrollImageSectionProps) => {
  const ref = useRef<HTMLDivElement>(null);
  
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
    container: ref
  });
  
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.8, 0.6]);

  return (
    <section 
      ref={ref} 
      className="relative overflow-hidden border-b border-gray-200"
      style={{ minHeight, position: 'relative' }}
    >
      <motion.div
        className="absolute inset-0 w-full h-[120%]"
        style={{ y }}
      >
            <img 
              src={imageUrl} 
              alt={imageAlt}
              fetchPriority="high"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
        <motion.div 
          className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/60"
          style={{ opacity: opacity }}
        />
      </motion.div>
      
      <div className="relative z-10 flex items-center justify-center" style={{ minHeight }}>
        <div className="text-center text-white px-4 max-w-4xl mx-auto py-20">
          {title && (
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-5xl lg:text-6xl font-bold font-['Playfair_Display'] mb-4 md:mb-6"
            >
              {title}
            </motion.h2>
          )}
          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base md:text-xl lg:text-2xl mb-6 md:mb-8 text-white/90"
            >
              {subtitle}
            </motion.p>
          )}
          {children && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {children}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};
